import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AdminCatalogCategory } from "../../api/fetchCatalogCategories";
import { SaveCatalogCategoryRequest } from "../../api/createCatalogCategory";

interface CatalogCategoryDialogProps {
  isOpen: boolean;
  category: AdminCatalogCategory | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (data: SaveCatalogCategoryRequest) => void;
}

export default function CatalogCategoryDialog({
  isOpen,
  category,
  isSaving,
  onClose,
  onSave,
}: CatalogCategoryDialogProps) {
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setName(category?.name ?? "");
      setTagline(category?.tagline ?? "");
      setSortOrder(category?.sortOrder ?? 0);
      setIsActive(category?.isActive ?? true);
    }
  }, [isOpen, category]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      tagline: tagline.trim() || null,
      sortOrder,
      isActive,
    });
  };

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {category ? "Edit Category" : "New Category"}
          </DialogTitle>
          <DialogDescription>
            Categories group entries on the member catalog page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
              htmlFor="category-name"
            >
              Name *
            </label>
            <Input
              className="dark:bg-gray-800"
              id="category-name"
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Marketing"
              value={name}
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
              htmlFor="category-tagline"
            >
              Tagline
            </label>
            <Textarea
              className="dark:bg-gray-800"
              id="category-tagline"
              onChange={(e) => setTagline(e.target.value)}
              placeholder="Short line shown under the category heading"
              rows={2}
              value={tagline}
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
              htmlFor="category-sort-order"
            >
              Sort order
            </label>
            <Input
              className="dark:bg-gray-800 max-w-[8rem]"
              id="category-sort-order"
              min={0}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              type="number"
              value={sortOrder}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div>
              <label
                className="block text-sm font-medium text-gray-900 dark:text-white"
                htmlFor="category-active"
              >
                Active
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Inactive categories are hidden from the member catalog.
              </p>
            </div>
            <Switch
              checked={isActive}
              id="category-active"
              onCheckedChange={setIsActive}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isSaving || !name.trim()}
            onClick={handleSave}
            type="button"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
